import React, { MutableRefObject, ReactNode, useCallback, useState } from 'react';
import { makeStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { Button } from '@mui/material';

import { CreditRisk } from '@types';
import { creditHttp, s3http } from '@network';
import { InitialCard, PdfPortal } from '@components';

interface Props {
  risk: CreditRisk;
  forwardRef: MutableRefObject<ReactNode>;
}

export const RiskTapeFile = ({ risk, forwardRef }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const cls = useStyles();

  const downloadLoanTapeFiles = async () => {
    setIsLoading(true);
    setIsGenerating(true);
  };

  const uploadCreditRiskPdf = useCallback(async (blob: Blob) => {
    try {
      const file = new File([blob], `${risk.credit.code}_risk_report.pdf`, {
        type: blob.type,
        lastModified: Date.now(),
      });

      const reportFile = await s3http.uploadFileToUrl(file, `/credit-risk/${risk.credit.id}/upload-risk-report`);

      await creditHttp.downloadLoanTapeFiles(risk.credit.id, reportFile.id);
      setIsLoading(false);
    } catch (error) {
      console.error('Error sending PDF:', error);
      setIsLoading(false);
    } finally {
      setIsGenerating(false);
    }
  }, [risk]);

  return (
    <InitialCard center="Loan Tape File" contentClass={cls.content} wrapperClass={cls.wrapper} extraPadding>
      <Button
        variant="outlined"
        color="primary"
        onClick={downloadLoanTapeFiles}
        disabled={isLoading}
        className={cls.button}
      >
        <div className={cls.buttonContent}>
          {isLoading
            ? <CircularProgress color="inherit" size={20} />
            : 'Download File'
          }
        </div>

      </Button>

      {isGenerating && (
        <PdfPortal
          risk={risk}
          onPdfGenerated={uploadCreditRiskPdf}
          isActive={isGenerating}
        />
      )}
    </InitialCard>
  );
};

const useStyles = makeStyles({
  wrapper: {
    height: '50%',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  button: {
    width: '100%!important',
    padding: '10px 15px!important',
    display: 'flex!important',
    gap: '8px !important',
    alignItems: 'center!important',
    background: '#002A77!important',
    color: '#fff',
    textTransform: 'none',
    borderRadius: 8,
    fontSize: 14,
    height: 48,
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    color: '#fff',
  },
  content: {
    display: 'flex',
    height: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 'auto',
  },
  info: {
    borderRadius: 7,
    backgroundColor: '#042E6B',
    marginTop: -44,
    height: 87,
    width: '100%',
    padding: 25,
    paddingRight: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoText: {
    color: '#FFF',
  },
  counter: {
    color: '#042E6B',
    backgroundColor: '#fff',
    width: 20,
    height: 20,
    borderRadius: '50%',
    lineHeight: '20px',
  },
  btnText: {
    color: '#FFF!important',
    fontSize: '16px!important',
  },
});
